import type { FlexiblePlacement, RecurrenceDaysOfWeekEnum, TimeWindow } from '../../api/generated';
import { DEFAULT_INTERVAL_WEEKS, timeWindowParts } from '../Bookings/shared';

const DATE_LENGTH = 10; // "YYYY-MM-DD"
const MINUTES_PER_HOUR = 60;
const HOURS_DECIMALS = 100; // round the hours hint to two decimals

// The form's view of a FlexiblePlacement: a bounded date window, an optional recurrence (one block
// per period) and an optional time-of-day restriction. Exception dates are deferred, so the form
// neither reads nor writes them.
export interface FlexiblePlacementFormState {
  windowStart: string; // YYYY-MM-DD
  windowEnd: string; // YYYY-MM-DD
  isRecurring: boolean;
  daysOfWeek: RecurrenceDaysOfWeekEnum[];
  intervalWeeks: number;
  isTimeRestricted: boolean;
  timeOfDayStart: string; // HH:mm
  timeOfDayEnd: string; // HH:mm
}

export interface FlexiblePlacementParts {
  windowStart: string;
  windowEnd: string;
  isRecurring: boolean;
  daysOfWeek: RecurrenceDaysOfWeekEnum[];
  intervalWeeks: number;
  timeOfDayWindow?: TimeWindow; // omitted = any working time
}

// Builds the FlexiblePlacement request body. `recurrence` is omitted for a one-off block and
// `timeOfDayWindow` for an any-time block (JSON.stringify drops undefined keys). `daysOfWeek` is
// typed as a Set by the generated client but the body is JSON, so we send a plain array cast
// through unknown — a real Set serializes to `{}`. Exception dates are deferred (always empty).
export const buildFlexiblePlacement = (parts: FlexiblePlacementParts): FlexiblePlacement => ({
  windowStart: parts.windowStart,
  windowEnd: parts.windowEnd,
  recurrence: parts.isRecurring
    ? {
        daysOfWeek: parts.daysOfWeek as unknown as Set<RecurrenceDaysOfWeekEnum>,
        intervalWeeks: parts.intervalWeeks
      }
    : undefined,
  timeOfDayWindow: parts.timeOfDayWindow,
  exceptionDates: []
});

// Loads a stored FlexiblePlacement into the form fields. A missing recurrence is a one-off; a
// missing timeOfDayWindow means any working time. `daysOfWeek` arrives as a plain JSON array
// (Array.from handles both array and Set).
export const splitFlexiblePlacement = (placement: FlexiblePlacement): FlexiblePlacementFormState => {
  const window = timeWindowParts(placement.timeOfDayWindow);
  return {
    windowStart: (placement.windowStart ?? '').slice(0, DATE_LENGTH),
    windowEnd: (placement.windowEnd ?? '').slice(0, DATE_LENGTH),
    isRecurring: Boolean(placement.recurrence),
    daysOfWeek: Array.from(placement.recurrence?.daysOfWeek ?? []),
    intervalWeeks: placement.recurrence?.intervalWeeks ?? DEFAULT_INTERVAL_WEEKS,
    isTimeRestricted: window !== null,
    timeOfDayStart: window?.startTime ?? '',
    timeOfDayEnd: window?.endTime ?? ''
  };
};

const parseMinutes = (time: string): number | null => {
  const match = /^(\d{2}):(\d{2})/.exec(time);
  if (!match) return null;
  return Number(match[1]) * MINUTES_PER_HOUR + Number(match[2]);
};

// Minutes between two HH:mm times, or null if either is missing/invalid. Used to check a duration
// fits inside the optional time-of-day window (the backend enforces the same rule).
export const minutesBetween = (startTime: string, endTime: string): number | null => {
  const start = parseMinutes(startTime);
  const end = parseMinutes(endTime);
  if (start === null || end === null) return null;
  return end - start;
};

// "3", "1.5" — whole hours stay integers, partial hours show up to two decimals. Drives the
// "= 3 t" readability hint next to the minutes input.
export const formatDurationHours = (minutes: number): string => {
  const hours = minutes / MINUTES_PER_HOUR;
  return Number.isInteger(hours)
    ? String(hours)
    : String(Math.round(hours * HOURS_DECIMALS) / HOURS_DECIMALS);
};
